import {
  faFilter,
  faTrashCan,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import { noop } from "lodash";
import { useMemo, useState } from "react";
import { SelectRangeEventHandler } from "react-day-picker";
import { useForm, FormProvider } from "react-hook-form";

import { Button } from "src/base-components/Button";
import {
  DateRange,
  DateRangePicker,
} from "src/base-components/DateRangePicker";
import { Icon } from "src/base-components/Icon";
import { CustomPopover } from "src/base-components/Popover";
import {
  EventFilterRequest,
  EventFilterRequestEnvironmentEnum,
} from "src/clients/features-retrieve";
import { EventFilterGroups } from "src/eventsCatalogue/filters/EventFilterGroups";
import { DEFAULT_FILTER_FORM_VALUES } from "src/eventsCatalogue/filters/constants";
import { EventFilterFormData } from "src/eventsCatalogue/filters/types";
import {
  convertFormDataToBackendFilter,
  getEventProperties,
} from "src/eventsCatalogue/filters/utils";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { findEventSchema } from "src/eventsCatalogue/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import { SubHeaderButton } from "src/layout/SubHeader";

type Props = {
  eventType: string;
  executionToken?: string | null;
  onOpenChange?: (isOpen: boolean) => void;
  onTriggerFilters?: (params: EventFilterRequest) => Promise<void>;
  onClearFilters?: () => void;
  dateRangeValue: DateRange | undefined;
  onDateRangeChange: SelectRangeEventHandler;
};

// Internal component that manages the form instance
const EventsFilterDropdownForm: React.FC<Props> = ({
  eventType,
  executionToken,
  onOpenChange,
  onTriggerFilters,
  onClearFilters,
  dateRangeValue,
  onDateRangeChange,
}) => {
  const [environment] = useEnvironment();
  const [isOpen, setIsOpen] = useState(false);
  const { data: eventSchemas } = useEventSchemas();
  const currentEvent = findEventSchema(eventType, eventSchemas);
  const eventProperties = useMemo(
    () => getEventProperties(currentEvent),
    [currentEvent],
  );

  const form = useForm<EventFilterFormData>({
    defaultValues: DEFAULT_FILTER_FORM_VALUES,
  });

  const formData = form.watch();
  const filterCondition = convertFormDataToBackendFilter(
    formData,
    eventProperties,
  );

  const isFilterApplied = isOpen || executionToken;

  const handleApplyFilters = () => {
    setIsOpen(false);
    onOpenChange?.(false);
    onTriggerFilters?.({
      event_type: eventType,
      environment: environment as EventFilterRequestEnvironmentEnum,
      start_timestamp: dateRangeValue?.from?.toISOString() || "",
      end_timestamp: dateRangeValue?.to?.toISOString() || "",
      filter_condition: filterCondition,
      entity_id: undefined,
    });
  };

  const handleClearFilters = () => {
    form.reset(DEFAULT_FILTER_FORM_VALUES);
    onClearFilters?.();
  };

  return (
    <CustomPopover
      button={() => (
        <SubHeaderButton
          icon={isFilterApplied ? faFilterSolid : faFilter}
          tooltip="All filters"
          tooltipPlacement="right"
          variant={isFilterApplied ? "active" : "default"}
          onClick={() => {
            setIsOpen(!isOpen);
            onOpenChange?.(!isOpen);
          }}
        />
      )}
      className="w-[700px]"
      dataLoc="events-filter-dropdown"
      isOpen={isOpen}
      offsetX={20}
    >
      <div className="divide-y divide-gray-200">
        {/* Header with date range */}
        <div className="flex items-center justify-between gap-x-3 px-4 py-2">
          <div className="flex items-center gap-x-3">
            <span className="whitespace-nowrap text-gray-700 font-inter-medium-12px">
              Show {currentEvent?.display_name_plural} for
            </span>
            <span className="cursor-pointer">
              <DateRangePicker
                value={dateRangeValue}
                usePredefinedDates
                onChange={onDateRangeChange ?? noop}
              />
            </span>
          </div>
          <CustomPopover.Button
            data-loc="events-filter-close-button"
            onClick={() => {
              setIsOpen(false);
              onOpenChange?.(false);
            }}
          >
            <Icon
              color="text-gray-500 hover:text-gray-700"
              icon={faXmark}
              size="xs"
            />
          </CustomPopover.Button>
        </div>

        {/* Filter Conditions */}
        <div className="p-4">
          <FormProvider {...form}>
            <EventFilterGroups eventType={eventType} />
          </FormProvider>
        </div>

        {/* Footer with Clear and Apply buttons */}
        <div className="flex justify-between px-4 py-2">
          <CustomPopover.Button>
            <Button
              dataLoc="events-filter-clear-button"
              iconLeft={faTrashCan}
              size="sm"
              variant="secondary"
              onClick={handleClearFilters}
            >
              Clear filters
            </Button>
          </CustomPopover.Button>
          <CustomPopover.Button>
            <Button
              dataLoc="events-filter-apply-button"
              size="sm"
              variant="primary"
              onClick={handleApplyFilters}
            >
              Apply
            </Button>
          </CustomPopover.Button>
        </div>
      </div>
    </CustomPopover>
  );
};

/**
 * Wrapper component that ensure that the EventsFilterDropdownForm is remounted when eventType changes.
 * The `key` prop is critical here - it ensures the form state is reset when switching
 * between different event types.
 *
 * DO NOT refactor this wrapper away - the key prop pattern is intentional and necessary.
 */
export const EventsFilterDropdown: React.FC<Props> = (props) => {
  return <EventsFilterDropdownForm key={props.eventType} {...props} />;
};
